import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../App';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentsPage = () => {
  const { user, sessionToken } = useAuth();
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    roll_no: '',
    class_id: '',
    parent_contact: '',
    photo: ''
  });

  useEffect(() => {
    fetchClasses();
    fetchStudents();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setClasses(response.data);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/students`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setStudents(response.data);
    } catch (error) {
      console.error('Failed to fetch students:', error);
      toast.error('Failed to load students');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingStudent) {
        await axios.put(`${API}/students/${editingStudent._id}`, formData, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        toast.success('Student updated successfully!');
      } else {
        await axios.post(`${API}/students`, formData, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        toast.success('Student added successfully!');
      }
      
      setDialogOpen(false);
      setEditingStudent(null);
      setFormData({ name: '', roll_no: '', class_id: '', parent_contact: '', photo: '' });
      fetchStudents();
    } catch (error) {
      console.error('Failed to save student:', error);
      toast.error('Failed to save student');
    }
  };

  const handleEdit = (student) => {
    setEditingStudent(student);
    setFormData({
      name: student.name,
      roll_no: student.roll_no,
      class_id: student.class_id,
      parent_contact: student.parent_contact,
      photo: student.photo || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (studentId) => {
    if (!window.confirm('Are you sure you want to delete this student?')) return;
    
    try {
      await axios.delete(`${API}/students/${studentId}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      toast.success('Student deleted successfully!');
      fetchStudents();
    } catch (error) {
      console.error('Failed to delete student:', error);
      toast.error('Failed to delete student');
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 font-heading mb-2" data-testid="students-title">Students</h1>
            <p className="text-slate-500 text-lg">Manage student information</p>
          </div>
          
          {user?.role === 'admin' && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-sky-500 hover:bg-sky-600 text-white rounded-full"
                  data-testid="add-student-btn"
                  onClick={() => {
                    setEditingStudent(null);
                    setFormData({ name: '', roll_no: '', class_id: '', parent_contact: '', photo: '' });
                  }}
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Student
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>{editingStudent ? 'Edit Student' : 'Add New Student'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="rounded-xl"
                      required
                      data-testid="student-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="roll_no">Roll Number</Label>
                    <Input
                      id="roll_no"
                      value={formData.roll_no}
                      onChange={(e) => setFormData({ ...formData, roll_no: e.target.value })}
                      className="rounded-xl"
                      required
                      data-testid="student-rollno-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="class_id">Class</Label>
                    <Select value={formData.class_id} onValueChange={(value) => setFormData({ ...formData, class_id: value })}>
                      <SelectTrigger className="rounded-xl" data-testid="student-class-select">
                        <SelectValue placeholder="Select a class" />
                      </SelectTrigger>
                      <SelectContent>
                        {classes.map((cls) => (
                          <SelectItem key={cls._id} value={cls._id}>
                            {cls.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="parent_contact">Parent Contact</Label>
                    <Input
                      id="parent_contact"
                      type="tel"
                      value={formData.parent_contact}
                      onChange={(e) => setFormData({ ...formData, parent_contact: e.target.value })}
                      className="rounded-xl"
                      required
                      data-testid="student-contact-input"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-sky-500 hover:bg-sky-600 rounded-full" data-testid="save-student-btn">
                    {editingStudent ? 'Update Student' : 'Add Student'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {students.map((student) => {
            const studentClass = classes.find(c => c._id === student.class_id);
            return (
              <Card key={student._id} className="p-6 rounded-2xl border-slate-100 bg-white hover:shadow-md transition-shadow" data-testid={`student-card-${student._id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-14 h-14 bg-sky-100 rounded-full flex items-center justify-center mr-3">
                      {student.photo ? (
                        <img src={student.photo} alt={student.name} className="w-14 h-14 rounded-full object-cover" />
                      ) : (
                        <span className="text-sky-600 font-semibold text-xl">{student.name.charAt(0)}</span>
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">{student.name}</h3>
                      <p className="text-sm text-slate-500">Roll No: {student.roll_no}</p>
                    </div>
                  </div>
                  {user?.role === 'admin' && (
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(student)}
                        data-testid={`edit-student-${student._id}`}
                      >
                        <Pencil className="w-4 h-4 text-slate-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(student._id)}
                        data-testid={`delete-student-${student._id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Class:</span>
                    <span className="font-medium text-slate-900">{studentClass?.name || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Parent Contact:</span>
                    <span className="font-medium text-slate-900">{student.parent_contact}</span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </Layout>
  );
};

export default StudentsPage;